import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { articles, categories, siteInfo } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const totalViews = categoryArticles.reduce((sum, a) => sum + a.views, 0);

  function formatStat(num: number): string {
    if (num >= 100000000) return `${(num / 100000000).toFixed(1)}亿`;
    if (num >= 10000) return `${(num / 10000).toFixed(0)}万`;
    return `${num}`;
  }

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${category.name} - ${siteInfo.name}`,
          url: `${siteInfo.domain}/category/${category.slug}`,
          description: category.description,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: siteInfo.domain,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: category.name,
            },
          ],
        }}
      />
      <Navbar />
      <main className="min-h-screen">
        <div className="pt-20 md:pt-24 pb-6 md:pb-8 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: category.name },
              ]}
            />

            <div className="flex gap-2 overflow-x-auto pb-3 mt-5 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`shrink-0 px-4 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
                    cat.slug === slug
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="lg:w-[40%]">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-background-300/60 rounded text-[11px] text-foreground-500 mb-4">
                  <i className={`${category.icon} text-xs`}></i>
                  分类浏览
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 leading-tight">
                  {category.name}
                  <span className="block text-lg md:text-xl font-normal text-foreground-500 mt-1">
                    {category.description}
                  </span>
                </h1>
                <p className="mt-6 text-sm text-foreground-600 leading-relaxed max-w-md">
                  本分类汇集了最新、最热的{category.name}相关爆料内容。每一条信息都经过团队严格核实，
                  确保为您呈现真实可靠的吃瓜资讯。
                </p>

                <div className="mt-6 flex gap-3">
                  {categories.filter(c => c.slug !== slug).slice(0, 3).map((c) => (
                    <Link
                      key={c.id}
                      to={`/category/${c.slug}`}
                      className="text-xs text-foreground-500 hover:text-primary-600 transition-colors border-b border-dashed border-foreground-300 hover:border-primary-400"
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="lg:w-[60%]">
                <div className="flex gap-6 mb-0 justify-end">
                  <div className="text-right">
                    <p className="text-2xl md:text-3xl font-black text-foreground-950">{categoryArticles.length}</p>
                    <p className="text-[11px] text-foreground-500 mt-0.5">篇爆料</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl md:text-3xl font-black text-foreground-950">{formatStat(totalViews)}</p>
                    <p className="text-[11px] text-foreground-500 mt-0.5">总阅读</p>
                  </div>
                </div>

                <div className="mt-6 flex gap-4">
                  <div className="w-[45%]">
                    <div className="rounded-lg overflow-hidden aspect-[4/5]">
                      <img
                        src="https://readdy.ai/api/search-image?query=Artistic%20editorial%20composition%20of%20a%20cracked%20smartphone%20screen%20showing%20blurred%20social%20media%20feed%20with%20breaking%20news%20alerts%2C%20dramatic%20dark%20background%20with%20subtle%20red%20and%20gold%20accents%2C%20mysterious%20celebrity%20gossip%20atmosphere%2C%20cinematic%20lighting%2C%204K%20editorial%20photography%2C%20no%20faces%20no%20text&width=600&height=750&seq=cat-feat-main&orientation=portrait"
                        alt={category.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="w-[55%]">
                    <div className="rounded-lg overflow-hidden aspect-[3/4]">
                      <img
                        src="https://readdy.ai/api/search-image?query=Dramatic%20editorial%20scene%20of%20a%20scattered%20pile%20of%20tabloid%20magazines%20and%20newspapers%20on%20a%20dark%20wooden%20floor%2C%20spotlight%20illuminating%20from%20above%2C%20shadows%20and%20highlights%20creating%20theatrical%20tension%2C%20warm%20golden%20and%20deep%20charcoal%20tones%2C%20investigative%20journalism%20aesthetic%2C%204K%20editorial%20photography%2C%20no%20faces%20no%20text&width=750&height=1000&seq=cat-feat-side&orientation=portrait"
                        alt={category.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2.5 mb-6">
              <h2 className="text-lg md:text-xl font-extrabold text-foreground-950">
                最新<span className="text-red-500">{category.name}</span>
              </h2>
              <span className="px-2.5 py-0.5 text-[11px] font-semibold bg-red-50 text-red-600 rounded-full">
                {categoryArticles.length}篇
              </span>
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    slug={article.slug}
                    title={article.title}
                    category={article.category}
                    coverImageSeq={article.coverImageSeq}
                    thumbnailPrompt={article.thumbnailPrompt}
                    publishedAt={article.publishedAt}
                    views={article.views}
                    isHot={article.isHot}
                    isBreaking={article.isBreaking}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500">该分类暂无爆料内容</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50 text-center">
          <div className="max-w-[700px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-extrabold text-foreground-950">
              不想错过{category.name}的<span className="text-accent-500">最新大瓜</span>？
            </h2>
            <p className="mt-3 text-sm text-foreground-500">
              开启推送通知，第一时间获取{category.name}相关爆料
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm rounded-lg transition-all"
            >
              回到首页吃更多瓜
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}